class FreeShippingMeter extends HTMLElement {
    connectedCallback() {
        this.hidden = true;
        this.progressBar = this.querySelector('[data-shipping-progress]');
        this.progressMeter = this.querySelector('[data-free-shipping-progress-meter]');
        this.messageElement = this.querySelector('[data-shipping-message]');
        this.onChange = () => this.initialize();
        this.addEventListener('change', this.onChange);
        this.initialize();
    }

    disconnectedCallback() {
        this.removeEventListener('change', this.onChange);
        this.request?.abort();
    }

    async initialize() {
        this.request?.abort();
        const request = new AbortController();
        this.request = request;
        try {
            const rule = await window.NavyCountryShipping.resolve();
            if (request.signal.aborted || !this.isConnected) return;
            if (!rule || rule.hidden || !Number.isFinite(rule.threshold) || rule.threshold <= 0 ||
                !/^[A-Z]{3}$/.test(rule.currency)) {
                this.hidden = true;
                return;
            }
            const timer = setTimeout(() => request.abort(), 8000);
            try {
                const root = window.Shopify?.routes?.root || '/';
                const response = await fetch(root + 'cart.js', { credentials: 'same-origin', cache: 'no-store', signal: request.signal });
                if (!response.ok) throw new Error('Cart unavailable');
                const cart = await response.json();
                if (request.signal.aborted || !this.isConnected) return;
                this.rule = rule;
                this.calculateProgress(cart);
            } finally {
                clearTimeout(timer);
            }
        } catch (error) {
            if (this.request === request) this.hidden = true;
        }
    }

    formatMoney(amount) {
        const currency = this.rule.currency;
        const format = window.Currency?.moneyFormats?.[currency]?.money_format ||
            (window.shop_currency === currency ? window.money_format : null);
        if (format && typeof window.Shopify?.formatMoney === 'function') {
            // Reuse the product currency format without converting this amount again.
            const formatted = document.createElement('template');
            formatted.innerHTML = window.Shopify.formatMoney(Math.round(amount * 100), format);
            return formatted.content.textContent;
        }
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
            style: 'currency', currency, currencyDisplay: 'narrowSymbol'
        }).format(amount);
    }

    calculateProgress(cart) {
        const currency = String(cart.currency || window.Shopify?.currency?.active || '').toUpperCase();
        const total = Number(cart.total_price);
        if (!this.rule || currency !== this.rule.currency || !Number.isFinite(total) || total < 0) {
            this.hidden = true;
            return;
        }
        const threshold = Math.round(this.rule.threshold * 100);
        if (!Number.isSafeInteger(threshold) || threshold <= 0) {
            this.hidden = true;
            return;
        }
        const eligible = total >= threshold;
        const percent = eligible ? 100 : Math.min(99, Math.floor(total * 100 / threshold));
        const text = window.free_shipping_text;
        this.freeShippingEligible = eligible ? 1 : 0;
        this.shipVal = eligible || total === 0 ? text.free_shipping_1 : text.free_shipping_2;
        if (total === 0) {
            this.messageElement.textContent = text.free_shipping_message + ' ' + this.formatMoney(threshold / 100) + '!';
        } else if (eligible) {
            this.messageElement.textContent = text.free_shipping_message_1;
        } else {
            const shippingLabel = document.createElement('span');
            shippingLabel.className = 'text';
            shippingLabel.textContent = text.free_shipping_message_4;
            this.messageElement.replaceChildren(
                text.free_shipping_message_2 + ' ' + this.formatMoney((threshold - total) / 100) + ' ' + text.free_shipping_message_3 + ' ',
                shippingLabel
            );
        }
        this.progressBar.classList.toggle('progress-hidden', total === 0);
        this.progressBar.classList.remove('none', 'progress-30', 'progress-60', 'progress-100', 'progress-free');
        this.progressBar.classList.add(eligible ? 'progress-free' : percent === 0 ? 'none' : percent <= 30 ? 'progress-30' : percent <= 60 ? 'progress-60' : 'progress-100');
        this.progressMeter.style.width = percent + '%';
        const percentLabel = this.progressMeter.querySelector('.text');
        if (percentLabel) percentLabel.textContent = percent + '%';
        this.querySelector('[role="progressbar"]').setAttribute('aria-valuenow', String(percent));
        this.hidden = false;
    }
}

function registerFreeShippingMeter() {
    if (!customElements.get('free-shipping-component')) {
        customElements.define('free-shipping-component', FreeShippingMeter);
    }
}

if (document.readyState !== 'complete') {
    document.addEventListener('DOMContentLoaded', registerFreeShippingMeter, { once: true });
} else {
    registerFreeShippingMeter();
}
